/**
 * AIActionButton — the ONLY violet button in the product.
 *
 * Authority:
 *   - docs/skills/DESIGN_LANGUAGE_SKILL.md Section 3 (violet = AI is acting).
 *   - docs/skills/DESIGN_LANGUAGE_SKILL.md Section 4 (AI action class — always
 *     icon + text; never icon-only; one per action bar).
 *   - docs/contracts/DESIGN_IMPLEMENTATION_CONTRACT.md Section 3.1.
 *
 * Tokens:    ACTION_BUTTONS.ai (DS_COLORS.ai.action / ai.actionDark hover),
 *            DS_TYPOGRAPHY.actionBtn, DS_MOTION.hover.primaryDuration.
 *
 * Props:
 *   icon     — required React node (e.g. <Sparkles size={16} />).
 *   children — required label text (passed from copy.js).
 *   onClick  — handler.
 *   disabled — bool.
 *   loading  — bool; shows inline spinner + disables interaction.
 *   type     — 'button' | 'submit'. Default 'button'.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '@/components/ui/button';
import {
  ACTION_BUTTONS,
  DS_COLORS,
  DS_SPACING,
  DS_TYPOGRAPHY,
  DS_MOTION,
} from '@/config/ui-invariants';

export function AIActionButton({
  icon,
  children,
  onClick,
  disabled = false,
  loading = false,
  type = 'button',
  ...rest
}) {
  const base = ACTION_BUTTONS.ai;
  return (
    <Button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      {...rest}
      style={{
        background: base.background,
        color: base.color,
        border: base.border,
        borderRadius: base.borderRadius,
        minHeight: base.minHeight,
        padding: `0 ${DS_SPACING.cardPadding}px`,
        display: 'inline-flex',
        alignItems: 'center',
        gap: DS_SPACING.buttonGap,
        transition: `background ${DS_MOTION.hover.primaryDuration}ms ${DS_MOTION.hover.primaryEase}`,
        ...DS_TYPOGRAPHY.actionBtn,
        ...(rest.style || {}),
      }}
      onMouseEnter={(e) => {
        if (!disabled && !loading) e.currentTarget.style.background = base.hoverBackground;
      }}
      onMouseLeave={(e) => {
        if (!disabled && !loading) e.currentTarget.style.background = base.background;
      }}
    >
      {loading ? (
        <span
          aria-hidden
          style={{
            width: 16,
            height: 16,
            border: `2px solid ${DS_COLORS.surface.white}`,
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'aiBtnSpin 0.8s linear infinite',
          }}
        />
      ) : (
        icon
      )}
      <span>{children}</span>
      <style>{`@keyframes aiBtnSpin { to { transform: rotate(360deg); } }`}</style>
    </Button>
  );
}

AIActionButton.propTypes = {
  icon: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  type: PropTypes.oneOf(['button', 'submit']),
};

export default AIActionButton;
